import { useEffect, useRef, useState, useCallback } from 'react';

// Strip template v2: 16 fields, vertical, REAL measured geometry from calibration photo
// All values relative (0-1) to strip length, independent of resolution/zoom
const STRIP_FIELDS = [
  { index: 1, parameter: 'pH', relCenterY: 0.0594, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 2, parameter: 'Gesamtalkalinität', relCenterY: 0.1189, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 3, parameter: 'Carbonat', relCenterY: 0.1774, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 4, parameter: 'Gesamthärte', relCenterY: 0.2359, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 5, parameter: 'Cyanursäure', relCenterY: 0.2943, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 6, parameter: 'Kupfer', relCenterY: 0.3538, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 7, parameter: 'Quecksilber', relCenterY: 0.4123, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 8, parameter: 'Gesamtchlor', relCenterY: 0.4708, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 9, parameter: 'Freies Chlor', relCenterY: 0.5302, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 10, parameter: 'Brom', relCenterY: 0.5887, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 11, parameter: 'Nitrit', relCenterY: 0.6472, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 12, parameter: 'Nitrat', relCenterY: 0.7066, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 13, parameter: 'Eisen', relCenterY: 0.7651, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 14, parameter: 'Chrom(VI)', relCenterY: 0.8236, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 15, parameter: 'Blei', relCenterY: 0.8821, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
  { index: 16, parameter: 'Fluorid', relCenterY: 0.9415, relHeight: 0.0389, relWidth: 0.3078, relCenterX: 0.5 },
];

type QualityState = {
  sharpness: number; // 0-1
  brightness: number; // 0-1
  stability: number; // 0-1
  isGood: boolean;
  message: string;
};

type CaptureState = 'idle' | 'aligning' | 'stabilizing' | 'capturing' | 'done';

type Props = {
  onCapture: (frames: Blob[]) => void;
  onClose: () => void;
};

export function LiveCamera({ onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameBufferRef = useRef<Blob[]>([]);
  const stableStartRef = useRef<number>(0);
  const prevSharpnessRef = useRef<number>(0);

  const [quality, setQuality] = useState<QualityState>({
    sharpness: 0, brightness: 0, stability: 0, isGood: false, message: 'Kamera wird gestartet…',
  });
  const [captureState, setCaptureState] = useState<CaptureState>('idle');
  const [capturedCount, setCapturedCount] = useState(0);
  const [error, setError] = useState<string>('');

  // Start camera
  useEffect(() => {
    let mounted = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!mounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCaptureState('aligning');
      } catch (err: any) {
        setError(`Kamera-Fehler: ${err.message || 'Zugriff verweigert'}`);
      }
    }

    startCamera();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Quality check loop
  useEffect(() => {
    if (captureState === 'done' || captureState === 'capturing') return;
    let raf = 0;

    function checkQuality() {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || !video.videoWidth) {
        raf = requestAnimationFrame(checkQuality);
        return;
      }

      const w = 320; // downsampled for analysis
      const h = Math.round(w * video.videoHeight / video.videoWidth);
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      ctx.drawImage(video, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const data = imageData.data;

      // Calculate brightness
      let sumBrightness = 0;
      let overexposed = 0;
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        sumBrightness += lum;
        if (lum > 0.92) overexposed++;
      }
      const pixelCount = data.length / 4;
      const brightness = sumBrightness / pixelCount;
      const overexposedRatio = overexposed / pixelCount;

      // Calculate sharpness (Laplacian variance)
      const gray = new Float32Array(w * h);
      for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }
      let lapVarSum = 0;
      let lapCount = 0;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const idx = y * w + x;
          const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
          lapVarSum += lap * lap;
          lapCount++;
        }
      }
      const lapVar = lapVarSum / lapCount;
      const sharpness = Math.min(lapVar / 800, 1); // normalize

      // Stability (how much sharpness changed)
      const stability = 1 - Math.min(Math.abs(sharpness - prevSharpnessRef.current) * 5, 1);
      prevSharpnessRef.current = sharpness;

      // Determine quality
      let isGood = true;
      let message = '';

      if (brightness < 0.15) {
        isGood = false;
        message = 'Zu dunkel – mehr Licht needed';
      } else if (brightness > 0.85 || overexposedRatio > 0.15) {
        isGood = false;
        message = 'Überbelichtet – Blitz vermeiden';
      } else if (sharpness < 0.15) {
        isGood = false;
        message = 'Kamera ruhig halten';
      } else if (stability < 0.7) {
        isGood = false;
        message = 'Kamera ruhig halten';
      } else if (sharpness < 0.3) {
        isGood = false;
        message = 'Näher herangehen';
      } else {
        message = 'Perfekt – Analyse startet…';
      }

      setQuality({ sharpness, brightness, stability, isGood, message });

      // Auto-capture logic
      if (isGood && captureState === 'aligning') {
        if (stableStartRef.current === 0) {
          stableStartRef.current = Date.now();
          setCaptureState('stabilizing');
        } else if (Date.now() - stableStartRef.current > 800) {
          setCaptureState('capturing');
        }
      } else if (!isGood && captureState === 'stabilizing') {
        stableStartRef.current = 0;
        setCaptureState('aligning');
      }

      raf = requestAnimationFrame(checkQuality);
    }

    raf = requestAnimationFrame(checkQuality);
    return () => cancelAnimationFrame(raf);
  }, [captureState]);

  // Auto-capture: capture 7 frames over ~2 seconds
  useEffect(() => {
    if (captureState !== 'capturing') return;
    frameBufferRef.current = [];
    setCapturedCount(0);
    let captured = 0;
    const targetFrames = 7;

    const captureFrame = () => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;

      const canvas = document.createElement('canvas');
      const maxDim = 1568;
      let w = video.videoWidth;
      let h = video.videoHeight;
      if (Math.max(w, h) > maxDim) {
        const ratio = maxDim / Math.max(w, h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, w, h);

      canvas.toBlob((blob) => {
        if (blob) {
          frameBufferRef.current.push(blob);
          captured++;
          setCapturedCount(captured);
          if (captured < targetFrames) {
            setTimeout(captureFrame, 250);
          } else {
            // Select best frames (just take all for now, backend will pick)
            setCaptureState('done');
            onCapture(frameBufferRef.current);
          }
        }
      }, 'image/jpeg', 0.92);
    };

    captureFrame();
  }, [captureState, onCapture]);

  // Manual capture fallback
  const handleManualCapture = useCallback(() => {
    setCaptureState('capturing');
  }, []);

  if (error) {
    return (
      <div className="camera-overlay">
        <div className="camera-error">
          <p>❌ {error}</p>
          <button onClick={onClose}>Schließen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-overlay">
      <div className="camera-container">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="camera-video"
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Strip template overlay */}
        <div className="strip-template-overlay">
          <div className="strip-outline">
            {STRIP_FIELDS.map((field) => (
              <div
                key={field.index}
                className="strip-field-box"
                style={{
                  top: `${field.relCenterY * 100}%`,
                  left: `${(field.relCenterX - field.relWidth / 2) * 100}%`,
                  width: `${field.relWidth * 100}%`,
                  height: `${field.relHeight * 100}%`,
                }}
              >
                <span className="field-num">{field.index}</span>
                <span className="field-name">{field.parameter}</span>
              </div>
            ))}
            <div className="strip-arrow">↑ OBEN</div>
          </div>
        </div>

        {/* Quality indicator */}
        <div className={`quality-indicator ${quality.isGood ? 'good' : 'warning'}`}>
          <span className="quality-message">{quality.message}</span>
          {captureState === 'stabilizing' && (
            <span className="stabilizing-dot">●</span>
          )}
          {captureState === 'capturing' && (
            <span className="capturing-progress">
              Aufnahme {capturedCount}/7…
            </span>
          )}
        </div>

        {/* Progress bars */}
        <div className="quality-bars">
          <div className="qbar">
            <span>Schärfe</span>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${quality.sharpness * 100}%` }} /></div>
          </div>
          <div className="qbar">
            <span>Helligkeit</span>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${quality.brightness * 100}%` }} /></div>
          </div>
          <div className="qbar">
            <span>Stabilität</span>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${quality.stability * 100}%` }} /></div>
          </div>
        </div>

        {/* Controls */}
        <div className="camera-controls">
          {captureState === 'aligning' && (
            <button className="btn-manual" onClick={handleManualCapture}>
              📸 Manuell auslösen
            </button>
          )}
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
      </div>
    </div>
  );
}
